import React, { type HTMLAttributes, type ReactNode, useId } from 'react';
import './AdminPage.scss';

type BadgeTone = 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'neutral';
type NoticeTone = 'info' | 'success' | 'warning' | 'danger';

interface AdminPageProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  description: string;
  actions?: ReactNode;
}

interface AdminPanelProps extends HTMLAttributes<HTMLElement> {
  title: string;
  description?: string;
  actions?: ReactNode;
  headingId?: string;
}

interface AdminBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  pill?: boolean;
}

interface AdminNoticeProps extends HTMLAttributes<HTMLDivElement> {
  tone: NoticeTone;
}

const join = (...classes: Array<string | undefined | false>) => classes.filter(Boolean).join(' ');

export function AdminPage({ title, description, actions, className, children, ...props }: AdminPageProps) {
  return (
    <main className={join('admin-page', className)} {...props}>
      <header className="admin-page__header">
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        {actions && <div className="admin-page__header-actions">{actions}</div>}
      </header>
      {children}
    </main>
  );
}

export function AdminPanel({
  title, description, actions, headingId, className, children, ...props
}: AdminPanelProps) {
  const generatedHeadingId = useId();
  const resolvedHeadingId = headingId ?? `admin-panel-${generatedHeadingId.replace(/:/g, '')}`;
  return (
    <section className={join('admin-panel', className)} aria-labelledby={resolvedHeadingId} {...props}>
      <div className="admin-panel__header">
        <div>
          <h3 id={resolvedHeadingId}>{title}</h3>
          {description && <p>{description}</p>}
        </div>
        {actions && <div className="admin-panel__actions">{actions}</div>}
      </div>
      <div className="admin-panel__content">{children}</div>
    </section>
  );
}

export function AdminBadge({ tone = 'neutral', pill = true, className, ...props }: AdminBadgeProps) {
  return <span className={join('admin-badge', `admin-badge--${tone}`, pill && 'admin-badge--pill', className)} {...props}/>;
}

export function AdminNotice({ tone, className, ...props }: AdminNoticeProps) {
  return <div className={join('admin-notice', `admin-notice--${tone}`, className)} role={tone === 'danger' ? 'alert' : 'status'} {...props}/>;
}

export function AdminEmptyState({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={join('admin-empty-state', className)} {...props}/>;
}
